'use client'

import * as React from 'react'
import { Calendar, DollarSign, Target, Users } from 'lucide-react'
import { Badge } from 'components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Progress } from 'components/ui/progress'
import { useProject } from './project-context'

function formatCurrency(value, currency = 'KES') {
  const amount = Number.isFinite(value) ? value : 0
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount)
}

function formatDate(value) {
  if (!value) return 'TBD'
  if (typeof value?.toDate === 'function') {
    return value.toDate().toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return 'TBD'
  return parsed.toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function toDateValue(value) {
  if (!value) return null
  if (typeof value?.toDate === 'function') {
    return value.toDate()
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed
}

function getTotalBudget(project) {
  if (Number.isFinite(project.totalBudget)) return project.totalBudget
  return (project.budgetItems || []).reduce((sum, item) => sum + Number(item.total || 0), 0)
}

export default function ProjectOverviewPage() {
  const { project } = useProject()

  const totalBudget = getTotalBudget(project)
  const spent = Number(project.spent || project.totalSpent || 0)
  const burnRate = totalBudget > 0 ? (spent / totalBudget) * 100 : 0

  const startDate = toDateValue(project.startDate)
  const endDate = toDateValue(project.endDate)
  const daysTotal =
    startDate && endDate
      ? Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
      : 0
  const daysElapsed = startDate
    ? Math.ceil((Date.now() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    : 0
  const timeProgress = daysTotal > 0 ? Math.min(100, Math.max(0, (daysElapsed / daysTotal) * 100)) : 0

  const members = Array.isArray(project.projectMembers) ? project.projectMembers : []
  const donors = Array.isArray(project.donors) ? project.donors : []


  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10">
                <DollarSign className="size-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Budget</p>
                <p className="text-lg font-bold text-foreground">{formatCurrency(totalBudget, project.currency)}</p>
                <p className="text-xs text-muted-foreground">
                  {spent > 0 ? `${formatCurrency(spent, project.currency)} spent` : 'No spend recorded'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10">
                <Target className="size-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Burn Rate</p>
                <p className="text-lg font-bold text-foreground">{burnRate.toFixed(1)}%</p>
                <p className="text-xs text-muted-foreground">
                  {totalBudget > 0 ? `${formatCurrency(totalBudget - spent, project.currency)} remaining` : 'Budget not set'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10">
                <Calendar className="size-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Timeline</p>
                <p className="text-lg font-bold text-foreground">{timeProgress.toFixed(0)}%</p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(project.startDate)} - {formatDate(project.endDate)}
                </p>
              </div>
            </div>
            <div className="mt-2">
              <Progress value={timeProgress} className="h-1.5" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10">
                <Users className="size-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Team</p>
                <p className="text-lg font-bold text-foreground">{members.length}</p>
                <p className="text-xs text-muted-foreground">members assigned</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">About This Project</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {project.description || 'No description provided.'}
            </p>
            <div className="mt-4 grid gap-3 grid-cols-2 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Type</p>
                <p className="font-medium text-foreground capitalize">
                  {(project.type || 'not specified').replace(/_/g, ' ')}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Currency</p>
                <p className="font-medium text-foreground">{project.currency || 'KES'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Principal Investigator</p>
                <p className="font-medium text-foreground">{project.principalInvestigatorName || 'Not set'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Project Manager</p>
                <p className="font-medium text-foreground">{project.projectManagerName || 'Not set'}</p>
              </div>
              {(project.type === 'donor_funded' || donors.length > 0) && (
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Donors</p>
                  <p className="font-medium text-foreground">
                    {donors.length > 0 ? donors.join(', ') : 'Not set'}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">SDG Alignment</CardTitle>
          </CardHeader>
          <CardContent>
            {project.sdgMappings.length === 0 ? (
              <p className="text-sm text-muted-foreground">No SDG mapping defined yet.</p>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {project.sdgMappings.map((sdgNum) => (
                  <div key={sdgNum} className="flex flex-col items-center gap-1 rounded-lg bg-muted/40 p-2">
                    <div className="flex size-8 items-center justify-center rounded-md bg-primary text-xs font-bold text-primary-foreground">
                      {sdgNum}
                    </div>
                    <span className="text-[10px] text-center font-medium text-foreground leading-tight">
                      SDG {sdgNum}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {project.strategicPillars.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-muted-foreground mb-2">Strategic Pillars</p>
                <div className="flex gap-2 flex-wrap">
                  {project.strategicPillars.map((pillar) => (
                    <Badge key={pillar} variant="secondary">
                      {pillar}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Project Team</CardTitle>
          </CardHeader>
          <CardContent>
            {members.length === 0 ? (
              <p className="text-sm text-muted-foreground">No members added yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {members.map((member) => (
                  <Badge key={member.id || member.name} variant="secondary">
                    {member.name || 'Member'}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
